import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Calendar as CalIcon, Plus, Clock, Video, Phone, MapPin, ChevronLeft, ChevronRight, Pencil, X, Repeat, Trash2 } from 'lucide-react';
import { formatName } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const DURATIONS = [
  { label: '30 min', value: 30 }, { label: '45 min', value: 45 }, { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 }, { label: '2 hours', value: 120 }, { label: '2.5 hours', value: 150 }, { label: '3 hours', value: 180 },
];
const STATUS_COLOR = { SCHEDULED: 'bg-[#3B82F6]', CHECKED_IN: 'bg-[#0D8B7D]', COMPLETED: 'bg-[#2E8B57]', CANCELLED: 'bg-[#D3544E]', NO_SHOW: 'bg-[#F5B041]' };
const STATUS_LABEL = { SCHEDULED: 'Scheduled', CHECKED_IN: 'Checked In', COMPLETED: 'Completed', CANCELLED: 'Cancelled', NO_SHOW: 'No Show' };
const TERMINAL = ['COMPLETED', 'NO_SHOW', 'CANCELLED'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EMPTY_FORM = { title: '', client_id: '', therapist_id: '', description: '', session_type: 'IN_PERSON', date: '', start_time: '10:00', end_time: '11:00', meeting_link: '', branch_id: '', duration: 60, session_for: 'BOTH' };

function calcEndTime(startTime, duration) {
  const [h, m] = startTime.split(':').map(Number);
  const em = h * 60 + m + duration;
  return `${String(Math.floor(em / 60)).padStart(2, '0')}:${String(em % 60).padStart(2, '0')}`;
}

export default function SessionsPage() {
  const { user } = useAuth();
  const isClient = user?.role === 'CLIENT';
  const isAdmin = user?.role === 'ADMIN';
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelScope, setCancelScope] = useState('THIS_ONLY');
  const [showNoShowModal, setShowNoShowModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, therapist_id: user?._id || '' });
  // Recurring state
  const [repeat, setRepeat] = useState(false);
  const [repeatFreq, setRepeatFreq] = useState('weekly');
  const [repeatDays, setRepeatDays] = useState([]);
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [repeatUnit, setRepeatUnit] = useState('weeks');
  const [endMode, setEndMode] = useState('after');
  const [endAfter, setEndAfter] = useState(10);
  const [endUntil, setEndUntil] = useState('');
  const [excludeDates, setExcludeDates] = useState([]);
  const [excludeInput, setExcludeInput] = useState('');

  const fetchData = useCallback(async () => {
    const [sRes, stRes] = await Promise.all([api.get('/sessions'), api.get('/sessions/stats/summary')]);
    setSessions(sRes.data); setStats(stRes.data);
  }, []);

  useEffect(() => { fetchData().catch(() => {}); }, [fetchData]);
  useEffect(() => {
    if (!isClient) {
      Promise.all([api.get('/clients'), api.get('/branches')]).then(([c, b]) => {
        setClients(c.data); setBranches(b.data);
      }).catch(() => {});
    }
  }, [isClient]);

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, therapist_id: form.therapist_id || user._id };
      if (repeat) {
        payload.repeat = true;
        payload.repeat_frequency = repeatFreq;
        payload.repeat_interval = repeatInterval;
        if (['weekly', 'bi_weekly'].includes(repeatFreq)) payload.repeat_days = repeatDays;
        if (repeatFreq === 'custom') { payload.repeat_unit = repeatUnit; if (repeatUnit === 'weeks') payload.repeat_days = repeatDays; }
        if (endMode === 'after') payload.end_after_sessions = endAfter;
        else if (endMode === 'until') payload.end_until_date = endUntil;
        else payload.end_after_sessions = 52;
        if (excludeDates.length) payload.exclude_dates = excludeDates;
      }
      const res = await api.post('/sessions', payload);
      const count = res.data?.count || 1;
      setShowModal(false);
      setForm({ ...EMPTY_FORM, therapist_id: user?._id || '' });
      setRepeat(false); setRepeatDays([]); setExcludeDates([]);
      fetchData();
      if (count > 1) alert(`${count} sessions created successfully!`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create session');
    }
  };

  const handleCheckIn = async (id) => { await api.put(`/sessions/${id}/check-in`); setShowDetail(null); fetchData(); };
  const handleNoShow = async () => { if (!showNoShowModal) return; await api.put(`/sessions/${showNoShowModal._id}/no-show`); setShowNoShowModal(null); setShowDetail(null); fetchData(); };
  const handleDeleteSession = async () => {
    if (!showDeleteModal) return;
    try { await api.delete(`/sessions/${showDeleteModal._id}`); setShowDeleteModal(null); setShowDetail(null); fetchData(); } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };
  const handleCancelSession = async () => {
    if (!showCancelModal || !cancelReason.trim()) return;
    if (showCancelModal.recurrence_group_id && cancelScope !== 'THIS_ONLY') {
      await api.put(`/sessions/${showCancelModal._id}/recurring-cancel`, { scope: cancelScope, reason: cancelReason.trim() });
    } else {
      await api.put(`/sessions/${showCancelModal._id}/cancel`, { reason: cancelReason.trim() });
    }
    setShowCancelModal(null); setCancelReason(''); setShowDetail(null); fetchData();
  };
  const updateStatus = async (id, status) => { await api.put(`/sessions/${id}`, { status }); setShowDetail(null); fetchData(); };

  const openEdit = (s) => {
    setEditForm({ title: s.title, description: s.description || '', session_type: s.session_type, date: s.date, start_time: s.start_time, end_time: s.end_time, meeting_link: s.meeting_link || '', branch_id: s.branch_id || '', duration: s.duration || 60 });
    setShowEditModal(s);
  };
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!showEditModal) return;
    try {
      await api.put(`/sessions/${showEditModal._id}`, editForm);
      setShowEditModal(null); setShowDetail(null); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update'); }
  };

  const canEdit = (s) => {
    if (TERMINAL.includes(s.status)) return false;
    if (isAdmin) return true;
    if (user?.role === 'THERAPIST' && s.therapist_id === user._id && ['SCHEDULED', 'CHECKED_IN'].includes(s.status)) return true;
    return false;
  };
  const canDelete = (s) => {
    if (isAdmin && !TERMINAL.includes(s.status)) return true;
    if (user?.role === 'THERAPIST' && s.therapist_id === user._id && s.status === 'SCHEDULED') return true;
    return false;
  };

  const selectedClient = clients.find(c => c._id === form.client_id);
  const isRelClient = selectedClient?.client_type === 'RELATIONSHIP';
  const getSessionForLabel = (s) => {
    if (!s.session_for || s.session_for === 'BOTH' || s.client_type !== 'RELATIONSHIP') return null;
    if (s.session_for === 'PARTNER1') return s.client_name?.split(' ')[0];
    if (s.session_for === 'PARTNER2') return s.partner_name?.split(' ')[0];
    return null;
  };

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const today = new Date().toISOString().split('T')[0];
  const getSessionsForDate = (day) => {
    const ds = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return sessions.filter(s => s.date === ds);
  };

  const toggleRepeatDay = (d) => setRepeatDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  return (
    <div className="space-y-5" data-testid="sessions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Session Calendar</h1>
        {!isClient && <Button onClick={() => setShowModal(true)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="new-session-btn"><Plus className="w-4 h-4 mr-2" />New Session</Button>}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="session-kpis">
        {[
          { label: 'Today', value: stats.today || 0, color: '#3B82F6' }, { label: 'Upcoming', value: stats.upcoming || 0, color: '#0A6F64' },
          { label: 'Checked In', value: stats.checked_in || 0, color: '#0D8B7D' }, { label: 'Completed', value: stats.completed || 0, color: '#2E8B57' },
          { label: 'No Show', value: stats.no_show || 0, color: '#F5B041' }, { label: 'Cancelled', value: stats.cancelled || 0, color: '#D3544E' },
        ].map((kpi, i) => (
          <Card key={i} className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${kpi.color}15` }}><CalIcon className="w-4 h-4" style={{ color: kpi.color }} /></div>
            <div><p className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{kpi.value}</p><p className="text-[11px] text-[#4A6B68]">{kpi.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      {/* Calendar */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="session-calendar">
        <CardContent className="p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}><ChevronLeft className="w-4 h-4" /></Button>
            <h3 className="font-semibold text-[#1A3330] font-['Outfit']">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
            <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (<div key={d} className="text-center text-xs font-medium text-[#4A6B68] py-2">{d}</div>))}
            {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const daySessions = getSessionsForDate(day);
              const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === today;
              return (
                <div key={day} className={`min-h-[60px] lg:min-h-[80px] p-1 rounded-xl border ${isToday ? 'border-[#0D8B7D] bg-[#0D8B7D]/5' : 'border-transparent hover:bg-[#F4FAF9]'} transition-colors`}>
                  <span className={`text-xs font-medium ${isToday ? 'text-[#0D8B7D]' : 'text-[#1A3330]'}`}>{day}</span>
                  <div className="space-y-0.5 mt-0.5">
                    {daySessions.slice(0, 2).map(s => (
                      <div key={s._id} onClick={() => setShowDetail(s)} className={`${STATUS_COLOR[s.status]} text-white text-[9px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80`} title={s.status === 'CANCELLED' && s.cancellation_reason ? `Cancelled: ${s.cancellation_reason}` : s.title} data-testid={`cal-session-${s._id}`}>
                        {s.start_time} {isClient ? s.therapist_name?.split(' ')[0] : s.client_name?.split(' ')[0]}
                      </div>
                    ))}
                    {daySessions.length > 2 && <div className="text-[9px] text-[#4A6B68]">+{daySessions.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Session List */}
      <div className="space-y-3" data-testid="session-list">
        {sessions.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 20).map(s => (
          <Card key={s._id} className="bg-white border-[#E5F2F1] rounded-2xl">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => setShowDetail(s)}>
                <div className="w-10 h-10 rounded-xl bg-[#F4FAF9] flex items-center justify-center">
                  {s.session_type === 'VIDEO' ? <Video className="w-4 h-4 text-[#0D8B7D]" /> : s.session_type === 'TELE' ? <Phone className="w-4 h-4 text-[#0D8B7D]" /> : <MapPin className="w-4 h-4 text-[#0D8B7D]" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A3330]">{s.title} {s.recurrence_group_id && <Repeat className="w-3 h-3 inline text-[#4A6B68]" />}</p>
                  <p className="text-xs text-[#4A6B68]">{s.date} · {s.start_time}-{s.end_time} · {isClient ? s.therapist_name : s.client_name}{getSessionForLabel(s) ? ` · ${getSessionForLabel(s)}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_COLOR[s.status]}/10 text-[10px] border-0`} style={{ color: STATUS_COLOR[s.status]?.replace('bg-[', '').replace(']', '') }}>{STATUS_LABEL[s.status]}</Badge>
                {s.status === 'CANCELLED' && s.cancellation_reason && <span className="text-[10px] text-[#D3544E] italic truncate max-w-[150px]" title={s.cancellation_reason}>({s.cancellation_reason})</span>}
                {!isClient && s.status === 'SCHEDULED' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-[#0D8B7D] text-xs h-7" onClick={() => handleCheckIn(s._id)} data-testid={`checkin-session-${s._id}`}>Check In</Button>
                    <Button size="sm" variant="ghost" className="text-[#F5B041] text-xs h-7" onClick={() => setShowNoShowModal(s)} data-testid={`noshow-session-${s._id}`}>No Show</Button>
                    <Button size="sm" variant="ghost" className="text-[#D3544E] text-xs h-7" onClick={() => { setShowCancelModal(s); setCancelReason(''); setCancelScope('THIS_ONLY'); }} data-testid={`cancel-session-${s._id}`}>Cancel</Button>
                  </div>
                )}
                {!isClient && s.status === 'CHECKED_IN' && (
                  <Button size="sm" variant="ghost" className="text-[#2E8B57] text-xs h-7" onClick={() => updateStatus(s._id, 'COMPLETED')} data-testid={`complete-session-${s._id}`}>Complete</Button>
                )}
                {s.session_type === 'VIDEO' && s.meeting_link && ['SCHEDULED', 'CHECKED_IN'].includes(s.status) && (
                  <a href={s.meeting_link} target="_blank" rel="noreferrer"><Button size="sm" className="bg-[#0D8B7D] text-white text-xs h-7 rounded-lg">Join</Button></a>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Session Detail Modal */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="session-detail-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Session Details</DialogTitle></DialogHeader>
          {showDetail && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-[#1A3330]">{showDetail.title}</h3>
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_COLOR[showDetail.status]}/10 text-[10px] border-0`} style={{ color: STATUS_COLOR[showDetail.status]?.replace('bg-[', '').replace(']', '') }}>{STATUS_LABEL[showDetail.status]}</Badge>
                  {canEdit(showDetail) && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(showDetail)} data-testid="edit-session-btn"><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                  )}
                  {canDelete(showDetail) && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setShowDeleteModal(showDetail); setDeleteReason(''); setShowDetail(null); }} data-testid="delete-session-btn"><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button>
                  )}
                </div>
              </div>
              <div className="bg-[#F4FAF9] p-3 rounded-xl space-y-1 text-sm">
                <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Client:</span> {showDetail.client_name}</p>
                <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Date:</span> {showDetail.date}</p>
                <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Time:</span> {showDetail.start_time} - {showDetail.end_time}</p>
                <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Type:</span> {showDetail.session_type}</p>
                {showDetail.meeting_link && <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Link:</span> <a href={showDetail.meeting_link} target="_blank" rel="noreferrer" className="text-[#0D8B7D] underline">{showDetail.meeting_link}</a></p>}
                {showDetail.description && <p className="text-[#1A3330]"><span className="text-[#4A6B68]">Notes:</span> {showDetail.description}</p>}
                {showDetail.recurrence_group_id && <p className="text-[10px] text-[#4A6B68] flex items-center gap-1"><Repeat className="w-3 h-3" />Part of recurring series</p>}
                {showDetail.cancellation_reason && <p className="text-[#D3544E] text-xs italic">Cancelled: {showDetail.cancellation_reason}</p>}
              </div>
              {/* Action buttons for ALL session types */}
              {!isClient && showDetail.status === 'SCHEDULED' && (
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-xs" onClick={() => handleCheckIn(showDetail._id)} data-testid="detail-checkin-btn">Check In</Button>
                  <Button size="sm" variant="outline" className="flex-1 rounded-xl text-xs border-[#F5B041] text-[#F5B041]" onClick={() => { setShowNoShowModal(showDetail); setShowDetail(null); }} data-testid="detail-noshow-btn">No Show</Button>
                  <Button size="sm" variant="outline" className="flex-1 rounded-xl text-xs border-[#D3544E] text-[#D3544E]" onClick={() => { setShowCancelModal(showDetail); setCancelReason(''); setCancelScope('THIS_ONLY'); setShowDetail(null); }} data-testid="detail-cancel-btn">Cancel</Button>
                </div>
              )}
              {!isClient && showDetail.status === 'CHECKED_IN' && (
                <Button size="sm" className="w-full bg-[#2E8B57] hover:bg-[#247A4A] text-white rounded-xl text-xs" onClick={() => updateStatus(showDetail._id, 'COMPLETED')} data-testid="detail-complete-btn">Mark Complete</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Session Modal */}
      <Dialog open={!!showEditModal} onOpenChange={() => setShowEditModal(null)}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="edit-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Session</DialogTitle></DialogHeader>
          {showEditModal && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="text-sm">Title</Label><Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div><Label className="text-sm">Type</Label>
                  <Select value={editForm.session_type} onValueChange={v => setEditForm({...editForm, session_type: v})}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="IN_PERSON">In-Person</SelectItem><SelectItem value="VIDEO">Video</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">Duration</Label>
                  <Select value={String(editForm.duration)} onValueChange={v => { const dur = parseInt(v); setEditForm(f => ({...f, duration: dur, end_time: calcEndTime(f.start_time, dur)})); }}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                    <SelectContent>{DURATIONS.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">Date</Label><Input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div><Label className="text-sm">Start Time</Label><Input type="time" value={editForm.start_time} onChange={e => { const st = e.target.value; setEditForm(f => ({...f, start_time: st, end_time: calcEndTime(st, f.duration)})); }} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div className="col-span-2"><Label className="text-sm">Branch</Label>
                  <Select value={editForm.branch_id} onValueChange={v => setEditForm({...editForm, branch_id: v})}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="text-sm">Meeting Link {editForm.session_type === 'VIDEO' && <span className="text-[#D3544E]">*</span>}</Label>
                  <Input value={editForm.meeting_link} onChange={e => setEditForm({...editForm, meeting_link: e.target.value})} required={editForm.session_type === 'VIDEO'} placeholder="https://meet.google.com/..." className="rounded-xl border-[#E5F2F1]" /></div>
                <div className="col-span-2"><Label className="text-sm">Description</Label><Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} /></div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-edit-session-btn">Save Changes</Button>
                <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowEditModal(null)}>Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* New Session Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg bg-white rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="new-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Schedule New Session</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateSession} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="session-title-input" /></div>
              <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Client</Label>
                <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})} required>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="session-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c._id} value={c._id}>{formatName(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm text-[#1A3330]">Type</Label>
                <Select value={form.session_type} onValueChange={v => setForm({...form, session_type: v})}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="session-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="IN_PERSON">In-Person</SelectItem><SelectItem value="VIDEO">Video</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm text-[#1A3330]">Duration</Label>
                <Select value={String(form.duration)} onValueChange={v => { const dur = parseInt(v); setForm(f => ({...f, duration: dur, end_time: calcEndTime(f.start_time, dur)})); }}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="session-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DURATIONS.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm text-[#1A3330]">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="session-date-input" /></div>
              <div><Label className="text-sm text-[#1A3330]">Start Time</Label><Input type="time" value={form.start_time} onChange={e => { const st = e.target.value; setForm(f => ({...f, start_time: st, end_time: calcEndTime(st, f.duration)})); }} required className="rounded-xl border-[#E5F2F1]" data-testid="session-start-time-input" /></div>
              <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Branch <span className="text-[#D3544E]">*</span></Label>
                <Select value={form.branch_id} onValueChange={v => setForm({...form, branch_id: v})} required>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="session-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Meeting Link {form.session_type === 'VIDEO' && <span className="text-[#D3544E]">*</span>}</Label>
                <Input value={form.meeting_link} onChange={e => setForm({...form, meeting_link: e.target.value})} required={form.session_type === 'VIDEO'} placeholder="https://meet.google.com/..." className="rounded-xl border-[#E5F2F1]" data-testid="session-meeting-link-input" /></div>
              <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} data-testid="session-description-input" /></div>
              {isRelClient && (
                <div className="col-span-2"><Label className="text-sm text-[#1A3330]">Session For</Label>
                  <Select value={form.session_for} onValueChange={v => setForm({...form, session_for: v})}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="session-for-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="BOTH">Both Partners</SelectItem><SelectItem value="PARTNER1">{formatName(selectedClient)} only</SelectItem><SelectItem value="PARTNER2">{selectedClient?.linked_partner_name || 'Partner 2'} only</SelectItem></SelectContent>
                  </Select>
                </div>
              )}

              {/* Recurring */}
              <div className="col-span-2 border-t border-[#E5F2F1] pt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={repeat} onChange={e => setRepeat(e.target.checked)} className="rounded border-[#E5F2F1]" data-testid="repeat-toggle" />
                  <Repeat className="w-4 h-4 text-[#0D8B7D]" />
                  <span className="text-sm font-medium text-[#1A3330]">Repeat</span>
                </label>
              </div>
              {repeat && (
                <div className="col-span-2 space-y-3 bg-[#F4FAF9] p-3 rounded-xl" data-testid="repeat-options">
                  <div><Label className="text-xs">Frequency</Label>
                    <Select value={repeatFreq} onValueChange={setRepeatFreq}>
                      <SelectTrigger className="rounded-xl border-[#E5F2F1] h-8 text-sm" data-testid="repeat-freq-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="bi_weekly">Bi-Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {['weekly', 'bi_weekly'].includes(repeatFreq) && (
                    <div><Label className="text-xs">Days</Label>
                      <div className="flex gap-1 mt-1">{DAYS.map((d, i) => (
                        <button key={d} type="button" onClick={() => toggleRepeatDay(i)} className={`w-8 h-8 rounded-lg text-[10px] border ${repeatDays.includes(i) ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'border-[#E5F2F1] text-[#1A3330]'}`} data-testid={`repeat-day-${d}`}>{d}</button>
                      ))}</div>
                    </div>
                  )}
                  {repeatFreq === 'custom' && (
                    <div className="flex gap-2 items-end">
                      <div><Label className="text-xs">Every</Label><Input type="number" min={1} value={repeatInterval} onChange={e => setRepeatInterval(parseInt(e.target.value) || 1)} className="w-16 rounded-xl border-[#E5F2F1] h-8 text-sm" /></div>
                      <Select value={repeatUnit} onValueChange={setRepeatUnit}><SelectTrigger className="rounded-xl border-[#E5F2F1] h-8 text-sm w-24"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="days">Days</SelectItem><SelectItem value="weeks">Weeks</SelectItem><SelectItem value="months">Months</SelectItem></SelectContent>
                      </Select>
                    </div>
                  )}
                  {repeatFreq === 'custom' && repeatUnit === 'weeks' && (
                    <div><Label className="text-xs">Days</Label>
                      <div className="flex gap-1 mt-1">{DAYS.map((d, i) => (
                        <button key={d} type="button" onClick={() => toggleRepeatDay(i)} className={`w-8 h-8 rounded-lg text-[10px] border ${repeatDays.includes(i) ? 'bg-[#0D8B7D] text-white border-[#0D8B7D]' : 'border-[#E5F2F1] text-[#1A3330]'}`}>{d}</button>
                      ))}</div>
                    </div>
                  )}
                  <div><Label className="text-xs">End</Label>
                    <div className="space-y-1.5 mt-1">
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="end" checked={endMode==='after'} onChange={() => setEndMode('after')} /> After <Input type="number" min={1} value={endAfter} onChange={e => setEndAfter(parseInt(e.target.value)||1)} className="w-14 h-7 rounded-lg border-[#E5F2F1] text-xs" disabled={endMode!=='after'} /> sessions</label>
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="end" checked={endMode==='until'} onChange={() => setEndMode('until')} /> Until <Input type="date" value={endUntil} onChange={e => setEndUntil(e.target.value)} className="h-7 rounded-lg border-[#E5F2F1] text-xs w-36" disabled={endMode!=='until'} /></label>
                      <label className="flex items-center gap-2 text-xs"><input type="radio" name="end" checked={endMode==='none'} onChange={() => setEndMode('none')} /> No end date</label>
                    </div>
                  </div>
                  <div><Label className="text-xs">Exclude Dates</Label>
                    <div className="flex gap-1 mt-1">
                      <Input type="date" value={excludeInput} onChange={e => setExcludeInput(e.target.value)} className="h-7 rounded-lg border-[#E5F2F1] text-xs flex-1" />
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={() => { if (excludeInput && !excludeDates.includes(excludeInput)) { setExcludeDates([...excludeDates, excludeInput]); setExcludeInput(''); } }}>Add</Button>
                    </div>
                    {excludeDates.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{excludeDates.map(d => (
                      <Badge key={d} className="bg-[#D3544E]/10 text-[#D3544E] text-[9px] border-0 cursor-pointer" onClick={() => setExcludeDates(prev => prev.filter(x => x !== d))}>{d} <X className="w-2.5 h-2.5 ml-0.5" /></Badge>
                    ))}</div>}
                  </div>
                </div>
              )}
            </div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="create-session-btn">Schedule Session</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Modal */}
      <Dialog open={!!showCancelModal} onOpenChange={() => setShowCancelModal(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="cancel-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Cancel Session</DialogTitle></DialogHeader>
          {showCancelModal && (
            <div className="space-y-4">
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{showCancelModal.title}</p>
                <p className="text-xs text-[#4A6B68]">{showCancelModal.client_name} · {showCancelModal.date} {showCancelModal.start_time}-{showCancelModal.end_time}</p>
              </div>
              {showCancelModal.recurrence_group_id && (
                <div><Label className="text-xs text-[#4A6B68]">This is a recurring session</Label>
                  <div className="space-y-1.5 mt-1">
                    {[['THIS_ONLY', 'Cancel this session only'], ['THIS_AND_FUTURE', 'Cancel this and all future sessions'], ['ALL', 'Cancel all sessions in series']].map(([v, l]) => (
                      <label key={v} className="flex items-center gap-2 text-xs"><input type="radio" name="scope" checked={cancelScope===v} onChange={() => setCancelScope(v)} />{l}</label>
                    ))}
                  </div>
                </div>
              )}
              <div><Label className="text-sm text-[#1A3330]">Cancellation Reason <span className="text-[#D3544E]">*</span></Label>
                <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} required rows={3} className="rounded-xl border-[#E5F2F1] mt-1" placeholder="Provide reason..." data-testid="cancel-reason-input" /></div>
              <div className="flex gap-2">
                <Button onClick={handleCancelSession} disabled={!cancelReason.trim()} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-cancel-session-btn">Confirm Cancellation</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowCancelModal(null)}>Go Back</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* No Show Modal */}
      <Dialog open={!!showNoShowModal} onOpenChange={() => setShowNoShowModal(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="noshow-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Mark as No Show</DialogTitle></DialogHeader>
          {showNoShowModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#4A6B68]">Mark this session as No Show?</p>
              <div className="bg-[#F5B041]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{showNoShowModal.title}</p>
                <p className="text-xs text-[#4A6B68]">{showNoShowModal.client_name} · {showNoShowModal.date}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleNoShow} className="flex-1 bg-[#F5B041] hover:bg-[#E59E29] text-white rounded-xl" data-testid="confirm-noshow-btn">Confirm</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowNoShowModal(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Session Modal */}
      <Dialog open={!!showDeleteModal} onOpenChange={() => setShowDeleteModal(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="delete-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Delete Session</DialogTitle></DialogHeader>
          {showDeleteModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#4A6B68]">Are you sure you want to permanently delete this session? This action cannot be undone.</p>
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{showDeleteModal.title}</p>
                <p className="text-xs text-[#4A6B68]">{showDeleteModal.client_name} · {showDeleteModal.date} {showDeleteModal.start_time}-{showDeleteModal.end_time}</p>
              </div>
              <div><Label className="text-sm">Reason <span className="text-[#D3544E]">*</span></Label>
                <Textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} rows={2} className="rounded-xl border-[#E5F2F1] mt-1" placeholder="Reason for deletion..." data-testid="delete-session-reason" /></div>
              <div className="flex gap-2">
                <Button onClick={handleDeleteSession} disabled={!deleteReason.trim()} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-delete-session-btn">Confirm Delete</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowDeleteModal(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
